import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../../utils/supabase";

interface AuthResponse {
  magic_link?: string;
  user?: {
    id: string;
    email?: string;
    first_name: string;
    last_name: string;
    role: string;
    school?: string;
    grade?: string;
  };
  user_data?: any;
  detailed_user_error?: string;
  message?: string;
  error?: string;
}

export default function CleverCallbackPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authMessage, setAuthMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const hasAttemptedAuth = useRef(false);

  useEffect(() => {
    // Prevent multiple authentication attempts
    if (hasAttemptedAuth.current) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const errorParam = urlParams.get("error");
    const originUrl = window.location.origin;

    // Handle OAuth errors from Clever
    if (errorParam) {
      setError(`Authentication failed: ${errorParam}`);
      setLoading(false);
      return;
    }

    if (!code) {
      setError("Missing authorization code from Clever.");
      setLoading(false);
      return;
    }

    const authenticateWithClever = async () => {
      try {
        hasAttemptedAuth.current = true;
        setLoading(true);
        setError(null);

        const { data, error: functionError } = await supabase.functions.invoke(
          "clever-auth-callback",
          {
            body: { code, originUrl },
          }
        );

        if (functionError) {
          throw new Error(
            functionError.message || "Failed to authenticate with Clever."
          );
        }

        const response: AuthResponse = data;

        if (response.error) {
          throw new Error(response.error || "Authentication failed");
        }

        // Check if we have a magic link to redirect to
        if (response.magic_link) {
          setAuthMessage("Redirecting to complete authentication...");

          // Store user data in localStorage if available
          if (response.user) {
            localStorage.setItem("user", JSON.stringify(response.user));
            localStorage.setItem("authProvider", "clever");
          }

          // Redirect to the magic link
          setTimeout(() => {
            window.location.href = response.magic_link;
          }, 1000);

          return;
        }

        // Set success message
        setAuthMessage(response.message || "Authentication successful!");

        // Store user data in localStorage
        if (response.user) {
          localStorage.setItem("user", JSON.stringify(response.user));
          localStorage.setItem("authProvider", "clever");
        }

        // Wait a moment to show success message, then redirect
        setTimeout(() => {
          // Redirect based on user role
          if (response.user?.role === "teacher") {
            navigate("/teacher/dashboard", { replace: true });
          } else if (response.user?.role === "student") {
            navigate("/dashboard", { replace: true });
          } else {
            navigate("/", { replace: true });
          }
        }, 2000);
      } catch (err: any) {
        console.error("Authentication error:", err);
        setError(
          err.message || "An unexpected error occurred during authentication."
        );
        setLoading(false);
      }
    };

    authenticateWithClever();
  }, []); // Empty dependency array since we're using ref to prevent multiple calls

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Authenticating with Clever...</p>
          <p className="text-gray-400 text-sm mt-2">
            Please wait while we verify your credentials
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Authentication Error</strong>
            <p className="mt-2">{error}</p>
          </div>
          <button
            onClick={() => navigate("/login-portal")}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (authMessage) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Success!</strong>
            <p className="mt-2">{authMessage}</p>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting you to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg">Processing...</p>
      </div>
    </div>
  );
}
